import get from 'lodash.get'
import {
  GAM_ADS_TAG_URL,
  GAM_SAMPLE_TAG_URL,
  GAM_SKIPPABLE_TAG_URL
} from '../../constants/video'
import { scrubValues } from '../../helpers/obj'

import { Flooring } from '../../flooring/Flooring'
import VideoPrebidManager from './VideoPrebidManager'
import { formatQueryString, queryParamHas } from '../../helpers/getQueryParam'
import { IVideoAdTagOptions } from '../../../typings/IVideoOptions'
import { DFP_ID } from '../../constants'

export const AUTOPLAY_CUSTOM = 3
export const AUTOPLAY_DESKTOP = 2
export const AUTOPLAY_MOBILE = 1
export const CATEGORY_SUBSTRING_LENGTH = 40
export const MOBILE_WIDTH_BREAKPOINT = 400

enum AdUnit {
  adhesion = 'adhesion_video',
  sticky = 'sticky_video',
  video = 'video'
}

let videoPrebidManager

// We're returning the `getAdTagHelper` function from `getAdTag` so we can
// expose webModel to all the helper functions
export function getAdTag(
  webModel: IWebViewModel
): (
  options: IVideoAdTagOptions,
  customParams?: object,
  skipPrebid?: boolean
) => Promise<string> {
  return async function getAdTagHelper(
    options: IVideoAdTagOptions,
    customParams: {} = {},
    skipPrebid: boolean = false
  ): Promise<string> {
    let prebid
    if (!window.pbjs) {
      const prebidjs = await import(
        /* webpackChunkName: "prebid" */ 'prebid.js'
      )
      prebid = prebidjs.default
    } else {
      prebid = window.pbjs
    }

    videoPrebidManager =
      videoPrebidManager || new VideoPrebidManager(options, webModel, prebid)

    if (queryParamHas('test', 'skippable')) {
      return GAM_SKIPPABLE_TAG_URL
    }

    if (options.test) {
      return GAM_SAMPLE_TAG_URL
    }

    const baseParams = getBaseParams(options)
    const prebidParams =
      !options.prebid || skipPrebid
        ? {}
        : await videoPrebidManager.getFormattedBidsFromPrebidAuction()
    const params = {
      correlator: Math.round(Date.now() / 1000),
      cust_params: encodeURIComponent(
        formatQueryString({
          ...baseParams,
          ...prebidParams,
          ...customParams
        })
      ),
      description_url: encodeURIComponent(options.permalink),
      env: 'vp',
      gdfp_req: '1',
      impl: 's',
      iu: encodeURIComponent(`/${DFP_ID}/${options.adunit}/video`),
      npa: options.npa || 0,
      output: 'xml_vast3',
      sz: '640x480',
      unviewed_position_start: '1',
      url: encodeURIComponent(window.location.href),
      vad_type: options.contentWidth < MOBILE_WIDTH_BREAKPOINT ? 'linear' : null
    }

    return `${GAM_ADS_TAG_URL}?${formatQueryString(params)}`
  }

  function getBaseParams(options: IVideoAdTagOptions): {} {
    const bucket = `${Math.floor(Math.random() * 100) + 1}`
    const date = new Date()

    return scrubValues({
      UR: Flooring.getVideoFloor({ country: webModel.country }) * 100,
      bucket,
      day: date.getUTCDate().toString(), // Day of Month (1-31)
      dow: date.getUTCDay().toString(), // Day of Week (0-6)
      google: options.google ? '1' : null,
      hour: date.getUTCHours().toString(), // Hour (0-23)
      month: String(date.getUTCMonth() + 1), // Month (1-12)
      player_group: options.playerGroup,
      player_version: options.playerVersion,
      slot: getAdUnitType(options),
      test: options.test,
      up_next: options.playlist.length ? 0 : null,
      video_beta: options.launcherGroup,
      video_id: options.slug,
      video_num: options.videoNumber,
      video_service: 3,
      wrapper_group: get(window, '$mediavine.web.model.versionGroup.name'),
      ...getConditionalBaseParams(options),
      ...options.customTargeting
    })
  }

  function getConditionalBaseParams(options: IVideoAdTagOptions): {} {
    const { categories, optouts } = options
    const params: any = {}

    if (optouts.length) {
      params.optout = optouts.join(',')
    }

    if (categories.length) {
      params.categories = categories
        .map((category) => category.substring(0, CATEGORY_SUBSTRING_LENGTH))
        .join(',')
    }

    if (options.autoplay) {
      // Autoplay different DFP based on player width (account for non-approved units)
      if (options.unitType === AdUnit.video) {
        params.ap =
          options.contentWidth >= MOBILE_WIDTH_BREAKPOINT
            ? AUTOPLAY_DESKTOP
            : AUTOPLAY_MOBILE
      } else {
        params.ap = AUTOPLAY_CUSTOM
      }
      params.ctp = '0'
    } else {
      // Click to Play
      params.ap = '0'
      params.ctp = '1'
    }

    return params
  }

  function getAdUnitType(options: IVideoAdTagOptions): string {
    // This is a hack to convince google this is a good adUnit with history
    if (options.autoplay && options.mobile) {
      return AdUnit.adhesion
    } else if (options.autoplay && !options.mobile) {
      return AdUnit.sticky
    }

    return options.unitType
  }
}
